import type { InputEvent, MouseButton } from '@br/protocol';
import type { Point } from './coords';

export type InputMouse = Extract<InputEvent, { t: 'input.mouse' }>;

export interface ModifierKeys {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

/** CDP modifier bitmask: Alt=1, Ctrl=2, Meta=4, Shift=8 */
export function modifiersOf(e: ModifierKeys): number {
  return (e.altKey ? 1 : 0) | (e.ctrlKey ? 2 : 0) | (e.metaKey ? 4 : 0) | (e.shiftKey ? 8 : 0);
}

/** DOM `MouseEvent.button` -> CDP button name. */
export function buttonName(button: number): MouseButton {
  switch (button) {
    case 0:
      return 'left';
    case 1:
      return 'middle';
    case 2:
      return 'right';
    case 3:
      return 'back';
    case 4:
      return 'forward';
    default:
      return 'none';
  }
}

/** DOM `MouseEvent.buttons` already uses the CDP bit layout (L1 R2 M4 back8 forward16). */
export function buttonsMask(buttons: number): number {
  return buttons & 31;
}

const MULTI_CLICK_MS = 500;
const MULTI_CLICK_PX = 5;

/**
 * Derives `clickCount` (1 = click, 2 = double, 3 = triple) from successive presses, the way a
 * native browser would: same button, within 500 ms, within 5 px.
 */
export class ClickCounter {
  private last?: { t: number; x: number; y: number; button: number; count: number };

  next(t: number, x: number, y: number, button: number): number {
    const l = this.last;
    const count =
      l &&
      l.button === button &&
      t - l.t <= MULTI_CLICK_MS &&
      Math.abs(x - l.x) <= MULTI_CLICK_PX &&
      Math.abs(y - l.y) <= MULTI_CLICK_PX
        ? Math.min(3, l.count + 1)
        : 1;
    this.last = { t, x, y, button, count };
    return count;
  }

  reset(): void {
    this.last = undefined;
  }
}

export interface MouseLike extends ModifierKeys {
  button: number;
  buttons: number;
}

export function buildMouseEvent(
  type: InputMouse['type'],
  tabId: string,
  pos: Point,
  e: MouseLike,
  clickCount: number,
): InputMouse {
  return {
    t: 'input.mouse',
    tabId,
    type,
    x: pos.x,
    y: pos.y,
    button: type === 'mouseMoved' ? 'none' : buttonName(e.button),
    buttons: buttonsMask(e.buttons),
    clickCount: type === 'mouseMoved' ? 0 : clickCount,
    modifiers: modifiersOf(e),
  };
}

/** Leading + trailing rate limit: coalesce intermediate moves, never lose the final position. */
export class MoveThrottle {
  private lastAt = -Infinity;
  private pending?: () => void;
  private timer?: ReturnType<typeof setTimeout>;
  constructor(private readonly minIntervalMs = 16) {}

  schedule(send: () => void): void {
    this.pending = send;
    if (this.timer !== undefined) return;
    const delay = this.minIntervalMs - (Date.now() - this.lastAt);
    if (delay <= 0) this.flush();
    else this.timer = setTimeout(() => this.flush(), delay);
  }

  cancel(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
    this.pending = undefined;
  }

  private flush(): void {
    this.timer = undefined;
    const send = this.pending;
    this.pending = undefined;
    this.lastAt = Date.now();
    send?.();
  }
}
